import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../seo/SEO';

/**
 * Conditions de service — version "particulier-artisan" qui couvre :
 *  - La prise de rendez-vous pour les pièces sur mesure
 *  - La réservation des pièces prêtes à porter par échange direct
 *    (WhatsApp, paiement en présentiel uniquement)
 *
 * Volontairement renommé "Conditions de service" plutôt que CGV pour
 * refléter qu'aucune transaction de vente à distance n'est conclue sur
 * le site lui-même (la vente se finalise en présentiel après devis ou
 * essayage). Cette précision place le site hors du champ strict de la
 * loi 31-08 sur la vente à distance.
 *
 * Le composant exporté garde son nom CGVPage pour ne pas casser les
 * imports existants. Le titre, l'URL footer et le contenu reflètent
 * le nouveau cadrage "conditions de service".
 */
export function CGVPage() {
  const { t } = useTranslation();

  return (
    <section className="max-w-editorial mx-auto px-6 sm:px-10 py-20">
      <SEO titleKey="seo.cgv.title" descriptionKey="seo.cgv.description" />
      <div className="grid grid-cols-12 gap-8 mb-16 border-b border-linen pb-12">
        <div className="col-span-12 md:col-span-3">
          <span className="editorial-number text-5xl block">N°CDS</span>
          <div className="editorial-label mt-3">— Service</div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h1 className="font-display text-[clamp(2.5rem,5vw,4.5rem)] leading-[0.95]">
            Conditions de service
          </h1>
          <p className="mt-6 editorial-label">Dernière mise à jour : mai 2026</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-9 md:col-start-4 space-y-12 font-body text-base text-ink-soft leading-relaxed">

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">1. Préambule</h2>
            <p>
              Le site <strong>faticaftan.com</strong> est édité par <strong>Fati Caftan</strong>,
              atelier artisanal individuel de couture traditionnelle marocaine. Il présente le
              catalogue de l'atelier (caftans, takchitas, djellabas, pièces prêtes à porter),
              les modalités de rendez-vous pour la confection sur mesure et les coordonnées de
              contact direct.
            </p>
            <p className="mt-3">
              <strong>Aucune transaction de vente à distance n'est conclue sur le site.</strong>{' '}
              Les pièces sont réservées par échange direct (WhatsApp, téléphone) puis
              finalisées en présentiel à l'atelier, après devis ou essayage. Le présent document
              encadre l'utilisation du site et la prise de contact qui en découle.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">2. Objet du site</h2>
            <p>L'atelier propose deux modalités&nbsp;:</p>
            <ul className="mt-3 space-y-1 list-disc list-inside">
              <li>
                <strong>Sur mesure</strong> — caftans, takchitas, djellabas et créations
                personnalisées confectionnées d'après les mesures prises lors d'un rendez-vous.
                Le site permet la prise de rendez-vous via le formulaire en ligne.
              </li>
              <li>
                <strong>Pièces prêtes à porter</strong> — modèles disponibles en tailles standard
                (XS à XXL). Le site présente le catalogue&nbsp;; la réservation se fait par
                clic sur le bouton « Réserver via WhatsApp » qui ouvre une conversation directe
                avec l'atelier, où s'organisent paiement et remise (en atelier, livraison locale,
                ou expédition après accord).
              </li>
            </ul>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">3. Prise de rendez-vous (sur mesure)</h2>
            <p>
              La cliente sélectionne une date et un créneau (matin, après-midi ou soir). Une
              confirmation est envoyée par email dans un délai de 24&nbsp;heures ouvrées. Trois
              créneaux maximum par jour&nbsp;: tout créneau déjà réservé est automatiquement
              indisponible côté formulaire.
            </p>
            <p className="mt-3">
              Le rendez-vous est gratuit. Il se déroule à l'atelier ou, sur demande, au domicile
              de la cliente dans la zone d'intervention de l'atelier (ville et alentours). À
              l'issue du rendez-vous, un devis détaillé est établi&nbsp;: prix, délai, tissus,
              finitions. La cliente est libre d'accepter ou non.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">4. Confection sur mesure</h2>
            <p>
              La confection démarre à réception d'un <strong>acompte</strong> (montant et
              modalités définis dans le devis, généralement entre 30 et 50 % du prix total). Le
              solde est dû à la livraison. Délai indicatif&nbsp;:
            </p>
            <ul className="mt-3 space-y-1 list-disc list-inside">
              <li><strong>Caftan / takchita / djellaba</strong> : 2 à 6 semaines selon la complexité du modèle</li>
              <li><strong>Tenue de mariage</strong> : 8 à 10 semaines minimum (plusieurs essayages prévus)</li>
            </ul>
            <p className="mt-3">
              Toute modification du modèle après acceptation du devis peut faire l'objet d'un
              avenant. Les pièces sur mesure ne sont ni reprises ni échangées une fois la
              confection lancée, en raison de leur personnalisation.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">5. Réservation des pièces prêtes à porter</h2>
            <p>
              Le bouton <em>« Réserver via WhatsApp »</em> ouvre une conversation pré-remplie
              (référence de la pièce, taille, prix). La conversation devient le canal officiel
              pour&nbsp;:
            </p>
            <ul className="mt-3 space-y-1 list-disc list-inside">
              <li>Confirmer la disponibilité de la pièce</li>
              <li>Convenir du mode de paiement (espèces, virement bancaire)</li>
              <li>Convenir du mode de remise (retrait atelier, livraison locale, expédition)</li>
            </ul>
            <p className="mt-3">
              La <strong>réservation devient ferme</strong> à la confirmation de l'atelier par
              WhatsApp. Tant qu'aucune confirmation explicite n'a été donnée, la pièce reste
              disponible pour d'autres clientes.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">6. Modalités de paiement</h2>
            <p>Les modes de paiement acceptés à ce jour&nbsp;:</p>
            <ul className="mt-3 space-y-1 list-disc list-inside">
              <li>Espèces (en atelier ou à la livraison locale)</li>
              <li>Virement bancaire (coordonnées communiquées par WhatsApp lors de la réservation)</li>
            </ul>
            <p className="mt-3 italic text-sm">
              Le paiement par carte bancaire en ligne (CMI) sera proposé dans une étape
              ultérieure, indépendamment du présent document. Aucune donnée bancaire n'est
              actuellement saisie ni traitée par le site.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">7. Annulation et report</h2>
            <p>
              <strong>Rendez-vous</strong>&nbsp;: la cliente peut annuler ou reporter un
              rendez-vous sans frais jusqu'à 24&nbsp;heures avant l'heure prévue, par email,
              téléphone ou WhatsApp.
            </p>
            <p className="mt-3">
              <strong>Réservation prêt à porter</strong>&nbsp;: tant qu'aucun paiement n'a été
              effectué, la réservation peut être annulée librement par message à l'atelier.
              Après paiement, l'annulation reste possible avant retrait/expédition&nbsp;; les
              modalités de remboursement sont convenues directement avec l'atelier (remboursement
              intégral en espèces ou virement, sous 14&nbsp;jours).
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">8. Garantie</h2>
            <p>
              Les pièces sont garanties contre tout vice de confection. Toute non-conformité
              constatée doit être signalée dans un délai raisonnable suivant la remise (idéalement
              dans les 7&nbsp;jours), par message WhatsApp accompagné de photos. L'atelier
              s'engage à proposer une reprise, une retouche ou un avoir selon la nature du
              défaut.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">9. Données personnelles</h2>
            <p>
              Les informations transmises via le site (nom, email, téléphone, message) sont
              traitées dans le respect de la loi marocaine n°09-08 et du RGPD européen. Pour le
              détail, consultez la{' '}
              <Link to="/privacy" className="text-brass hover:text-brass-dark underline">
                politique de confidentialité
              </Link>.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">10. Propriété intellectuelle</h2>
            <p>
              Les créations originales de l'atelier (motifs, broderies, coupes signatures) restent
              la propriété intellectuelle de l'éditrice. La reproduction commerciale ou la copie
              des modèles est interdite sans autorisation écrite préalable.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">11. Modification des conditions</h2>
            <p>
              L'éditrice se réserve la possibilité de modifier les présentes conditions de
              service à tout moment, pour refléter une évolution du site, des modalités
              d'atelier ou de la législation. La date en tête de page indique la dernière
              version applicable.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">12. Droit applicable</h2>
            <p>
              Les présentes conditions sont régies par le droit marocain. Tout différend né de
              leur interprétation ou de leur application fera l'objet, en cas d'échec d'une
              résolution amiable, d'une saisine des juridictions marocaines compétentes du
              ressort de la résidence de l'éditrice.
            </p>
          </article>

          <p className="pt-4">
            <Link to="/" className="brass-link editorial-label">
              ← {t('productDetail.back')}
            </Link>
          </p>

        </div>
      </div>
    </section>
  );
}
